import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const Navigation = ({
  assetsData = [],
  assetsBaseUrl = 'https://dyararts.com'
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [miniGallerySortOrder, setMiniGallerySortOrder] = useState('latest');
  const navigate = useNavigate();
  const location = useLocation();

  // Generate thumbnail URL by inserting _th before file extension
  const getThumbnailUrl = (filename) => {
    if (!filename) return '';
    const thumbFilename = filename.replace(/(\.\w+)$/, '_th$1');
    return `${assetsBaseUrl}${thumbFilename}`;
  };

  // Toggle menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Handle home navigation with smart scroll behavior
  const handleHomeNavigation = (e) => {
    e.preventDefault();
    setIsMenuOpen(false);

    navigate('/');
  };

  // Handle about navigation - scroll to top if already on about page
  const handleAboutNavigation = (e) => {
    e.preventDefault();
    setIsMenuOpen(false);
    
    // Check if we're already on the about page
    if (location.pathname === '/about') {
      // Already on about page, just scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Navigate to about page
      navigate('/about');
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isMenuOpen &&
          !e.target.closest('.app-icon') &&
          !e.target.closest('.slide-out-menu')) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <>
      {/* Fixed App Icon - Toggles menu */}
      <div
        className="app-icon"
        onClick={toggleMenu}
      >
      </div>

      {/* Global Slide-out Menu */}
      <div className={`slide-out-menu ${isMenuOpen ? 'menu-open' : 'menu-closed'}`}>
        <div className="menu-row">
          <ul>
            <li>
              <a href="/" onClick={handleHomeNavigation}>
                Home
              </a>
            </li>
            <li>
              <a href="/about" onClick={handleAboutNavigation}>
                About
              </a>
            </li>
            <li>
              <Link to="/about#contact" onClick={() => setIsMenuOpen(false)}>
                Contact
              </Link>
            </li>
            <li>
              <Link to="/news" onClick={() => setIsMenuOpen(false)}>
                News
              </Link>
            </li>
          </ul>
        </div>

        <div className="menu-row">
          <div className="menu-row-title-container">
            <div className="menu-row-title">Follow Me</div>
          </div>
          <ul>
            <li>
              <a href="https://youtube.com/@dyararts" target="_blank" rel="noopener noreferrer" className="youtube-link">
                YouTube
              </a>
            </li>
          </ul>
        </div>

        <div className="menu-row">
          <div className="menu-row-title-container">
            <div className="menu-row-title">Artworks</div>
            <div className="mini-gallery-sort-options">
              SORT:
              <span
                onClick={() => setMiniGallerySortOrder('latest')}
                className={miniGallerySortOrder === 'latest' ? 'active' : ''}
              >
                Latest
              </span>
              <span
                onClick={() => setMiniGallerySortOrder('earliest')}
                className={miniGallerySortOrder === 'earliest' ? 'active' : ''}
              >
                Earliest
              </span>
            </div>
          </div>
          <div className="mini-gallery">
            {assetsData
              .filter(asset => asset.slug) // Only show assets with slugs
              .sort((a, b) => {
                // Sort by artwork-id
                const idA = a['artwork-id'] || '';
                const idB = b['artwork-id'] || '';

                if (miniGallerySortOrder === 'latest') {
                  // Latest first: reverse order (descending by ID)
                  return idB.localeCompare(idA);
                } else {
                  // Earliest first: normal order (ascending by ID)
                  return idA.localeCompare(idB);
                }
              })
              .map((asset) => (
                <Link
                  key={asset.slug}
                  to={`/art/${asset.slug}`}
                  className="mini-gallery-thumb"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <img
                    src={getThumbnailUrl(asset.filename)}
                    alt={asset.title || 'Artwork'}
                    loading="lazy"
                  />
                </Link>
              ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
